import React from 'react';
import styled from 'styled-components';
import { HeartButton } from '../../ui/HeartButton';
import { ResultsProductCardItem } from './types';
import { AddedLinkGlyph } from '../../../theaWeb/board/AddedLinkGlyph';
import { addedLinkGridLine, savedItemAccessibleName } from '../../../theaWeb/lib/addedLink';

export interface ResultsProductCardProps {
  item: ResultsProductCardItem;
  liked: boolean;
  /** Eager-load the image for the LCP card. */
  priority?: boolean;
  onClick?: () => void;
  onSaveClick?: () => void;
  /**
   * Dismiss ("not for me") for this card. When provided, the card renders the
   * visible X chip in the image's top-right; omit it and no X is rendered, so
   * legacy carousel surfaces stay byte-for-byte unchanged (THE-515).
   */
  onDismiss?: () => void;
  /**
   * Optional node rendered flush beneath the image, ABOVE the title/meta — used
   * by the board surface to attach a "connected drawer" Like bar to the bottom
   * of the photo (instead of an overlay that covers it). When provided, the
   * image's bottom corners go flat so the footer joins seamlessly. Legacy
   * carousel callers omit it and keep the fully-rounded image, unchanged.
   */
  imageFooter?: React.ReactNode;
  /**
   * Optional node rendered as the LAST child of the image frame, i.e. a
   * positioned overlay ON the photo — used by the board feed for the
   * "More like this" sticker. It is a sibling of (and after) the transparent
   * OpenButton on purpose: nesting it inside OpenButton would be invalid
   * button-in-button markup, and placing it before OpenButton would leave it
   * painted underneath and untappable. Overlays are responsible for their own
   * absolute positioning and for stacking above the dismiss chip (z-index 10).
   * Omitted by every surface but the board feed, which keeps those surfaces
   * byte-for-byte unchanged.
   */
  imageOverlay?: React.ReactNode;
  /**
   * Crop the photo to the board's wide 6:5 instead of the default tall 4:5.
   * Shorter cards fit roughly a third more gifts on a phone screen, which is
   * why the recipient board adopted it (Kate 2026-05-12) and why the gift
   * guide followed on THE-684.
   *
   * This is a PROP and not a descendant-selector override on purpose. The
   * board used to reach in with `& > div:first-child > div > div:first-child`,
   * which encodes this file's DOM nesting in a caller's stylesheet and fails
   * SILENTLY — as a slightly-too-tall photo, not an error — the moment a
   * wrapper element is added or removed here. Both surfaces now go through
   * this prop, so the crop is one thing in one place.
   */
  wideImage?: boolean;
  /** Optional ref attached to the card root. Used by ranker-telemetry impression
   *  tracking; safe to omit elsewhere. */
  cardRef?: React.Ref<HTMLDivElement | null>;
}

const Root = styled.div`
  position: relative;
  width: 100%;
  /* Synchronized "open" hover (Baymard unified-hit-area): hovering the photo's
     open-button OR the title/brand/price both re-assert the title's own ink
     color, so the whole open-area reacts as one (cursor: pointer on Meta is
     the actual affordance cue). THE-1185, QA round 5: this used to shift the
     title to clay/primary on hover, which is outside the board's "2 text
     colors" rule — hover is not a carve-out, so it now reads the same
     foreground the title always does. The heart / overflow / save-bar are a
     separate
     "save" path and never match here — the heart is a sibling of the open-button
     (so hovering it isn't hovering the open-button), and the save-bar sits outside
     [data-open-meta]. Desktop only; touch has no hover. (:has() — Safari 16+, our
     floor; older engines simply get no hover cue, which is a clean degrade.) */
  @media (hover: hover) {
    &:has([data-open-photo]:hover) [data-open-meta] > p:first-child,
    &:has([data-open-meta]:hover) [data-open-meta] > p:first-child {
      color: hsl(var(--foreground));
    }
  }
`;

/* Presentational container only; the open action lives on the OpenButton
   inside ImageFrame so it's keyboard-operable without wrapping the heart /
   overflow buttons inside another interactive element (invalid nesting). */
const Inner = styled.div``;

/**
 * Transparent button covering the IMAGE — the keyboard-operable open target
 * (WCAG 2.1.1). Rendered inside ImageFrame AFTER the <img> and BEFORE the
 * heart / overflow, with NO z-index, so by stacking order it paints above the
 * image (a tap there opens) but below those later-in-DOM positioned controls,
 * which stay clickable. The product opener before this was a mouse-only
 * <div onClick>, unreachable by keyboard / AT. a11y row 118.
 *
 * (An earlier attempt put a full-card overlay in Root; that failed in a real
 * browser — ImageFrame is position:relative, so its whole subtree painted
 * above a Root-level sibling. Scoping the button inside ImageFrame fixes it.)
 */
const OpenButton = styled.button`
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  border-radius: 16px;
  -webkit-tap-highlight-color: transparent;
  &:focus-visible {
    outline: 2px solid hsl(var(--ring));
    outline-offset: -2px;
  }
`;

/**
 * Note: rounded corners + overflow:hidden moved off ImageFrame onto Img so the
 * overflow menu (anchored on the bottom-right of the image area) can paint
 * outside the visible image without being clipped (bug #21). The image itself
 * still clips to the rounded shape via its own border-radius.
 */
const ImageFrame = styled.div<{ $wide?: boolean }>`
  aspect-ratio: ${({ $wide }) => ($wide ? '6 / 5' : '4 / 5')};
  background: transparent;
  position: relative;
`;

const Img = styled.img<{ $flatBottom?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: ${({ $flatBottom }) => ($flatBottom ? '16px 16px 0 0' : '16px')};
  background: hsl(var(--muted));
`;

// THE-515 restores the visible dismiss affordance that boards bucket 6 had
// removed. Geometry deliberately mirrors HeartButton (top/right: 8px, 36px
// white chip) so the two top-right controls are interchangeable: the board
// surface hides the heart and shows this, the legacy carousel does the reverse
// by simply not passing `onDismiss`.
//
// THE-551 item #2 asked for the X to move LEFT, away from the mobile interest
// rail, and an intermediate build shipped `right: 40px` below 1024px to clear
// the rail's 44px hit area. The board then asked for the corner back, and the
// follow-up (same ticket) moved interest navigation OUT of the right edge
// entirely — `BoardInterestRail` is gone, the interests are a scroll-spy header
// under the board header now. With nothing fixed at the right edge at any
// breakpoint, the overlap that forced the inset no longer exists, so the X
// returns to the corner and mirrors the heart again. If a fixed right-edge
// surface is ever reintroduced, re-check this offset against its hit area, not
// just its paint.
const DismissButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  box-shadow: ${({ theme }) => theme.shadow.card};
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 150ms ease, background 150ms ease;
  &:hover {
    transform: scale(1.06);
    background: rgba(255, 255, 255, 1);
  }
  &:active {
    transform: scale(0.96);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const XIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="hsl(var(--foreground) / 0.7)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Meta = styled.div`
  margin-top: 8px;
  padding: 0 4px;
  /* The title/brand/price also open the product (not just the photo). Mouse
     convenience — the keyboard/AT open path is the image OpenButton above, so
     this block is deliberately not a separate tab stop / SR control. The hover
     cue lives on Root so the photo and this text light up together (synchronized
     hit-area, per Baymard). */
  cursor: pointer;
`;

/* THE-1185 — the board's "tile" role (shared with tile titles/counts
   elsewhere on the board) and the one ink color (theme.color.foreground,
   Kate's approved #38322E), instead of a one-off 16px/600 hex. */
const Title = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.text.tile.mobile};
  font-weight: ${({ theme }) => theme.weight.semibold};
  color: ${({ theme }) => theme.color.foreground};
  transition: color var(--dur-press) ease;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (min-width: 1024px) {
    font-size: ${({ theme }) => theme.text.tile.desktop};
  }
`;

const Brand = styled.p`
  margin: 2px 0 0 0;
  font-size: ${({ theme }) => theme.text.small.mobile};
  color: ${({ theme }) => theme.color.mutedForeground};

  @media (min-width: 1024px) {
    font-size: ${({ theme }) => theme.text.small.desktop};
  }
`;

// THE-1084 PR4, spec §4: an added link's image area is "the gift icon on a
// neutral fill. No photo." — never a broken <img src="">.
const AddedLinkImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(var(--muted-foreground));
  background: hsl(var(--muted));
  border-radius: 16px;
`;

// Spec §4: named → the store address (no price, ever); unnamed → the raw
// link itself, truncated — "what tells two unnamed items apart". One line,
// same slot the ordinary Brand/Price rows use.
const LinkLine = styled.p`
  margin: 2px 0 0 0;
  font-size: ${({ theme }) => theme.text.small.mobile};
  color: ${({ theme }) => theme.color.mutedForeground};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 1024px) {
    font-size: ${({ theme }) => theme.text.small.desktop};
  }
`;

/* THE-1185 — was 500, a third weight between the board's regular/semibold
   pair; folds into regular so Price reads as the rest of this secondary
   meta block (Brand/LinkLine) does, not louder than them. */
const Price = styled.p`
  margin: 2px 0 0 0;
  font-size: ${({ theme }) => theme.text.small.mobile};
  font-weight: ${({ theme }) => theme.weight.regular};
  color: ${({ theme }) => theme.color.mutedForeground};

  @media (min-width: 1024px) {
    font-size: ${({ theme }) => theme.text.small.desktop};
  }
`;

export const ResultsProductCard: React.FC<ResultsProductCardProps> = ({
  item,
  liked,
  priority = false,
  onClick,
  onSaveClick,
  onDismiss,
  imageFooter,
  imageOverlay,
  wideImage = false,
  cardRef,
}) => (
  // styled-components' LegacyRef typing predates strict null-tracking; cast to
  // satisfy the older Ref<HTMLDivElement> signature without requiring callers
  // to use a non-null ref.
  <Root ref={cardRef as React.Ref<HTMLDivElement>}>
    <Inner>
      <ImageFrame $wide={wideImage}>
        {item.kind === 'LINK' ? (
          <AddedLinkImage aria-hidden="true">
            <AddedLinkGlyph size="25%" />
          </AddedLinkImage>
        ) : (
          <Img
            src={item.imageUrl}
            alt={item.title}
            loading={priority ? 'eager' : 'lazy'}
            $flatBottom={imageFooter != null}
          />
        )}
        <OpenButton
          type="button"
          data-open-photo
          aria-label={`Open ${savedItemAccessibleName(item)}`}
          onClick={onClick}
        />
        {onDismiss && (
          <DismissButton
            type="button"
            aria-label="Remove item"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <XIcon />
          </DismissButton>
        )}
        <HeartButton
          liked={liked}
          onClick={(e) => {
            e.stopPropagation();
            onSaveClick?.();
          }}
        />
        {imageOverlay}
      </ImageFrame>
      {imageFooter}
      <Meta data-open-meta onClick={onClick}>
        <Title>{item.title}</Title>
        {item.kind === 'LINK' && item.addedLink ? (
          <LinkLine>{addedLinkGridLine(item.addedLink)}</LinkLine>
        ) : (
          <>
            {item.brand && <Brand>{item.brand}</Brand>}
            {item.price != null && <Price>${Math.round(item.price)}</Price>}
          </>
        )}
      </Meta>
    </Inner>
  </Root>
);
